// Import 3rd-party dependencies
import { Component, OnInit, OnDestroy } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, share, startWith, switchMap, tap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { SubSink } from 'subsink';

// Import project-specific dependencies
import { TRENDING_ITCONNECT_COMPONENT_CONSTS } from '@app/config/constants';
import { IItArticles, SearchDataService } from '@app/services/searchdata/searchdata.service';
import { PeopleService } from '@app/services/people/people.service';
import { QuickLinksService } from '@app/services/quick-links/quick-links.service';
import { QUICK_LINKS_COMPONENT_CONSTS } from '@app/config/constants';

@Component({
  selector: 'app-trending-itconnect',
  templateUrl: './trending-itconnect.component.html',
  styleUrls: ['./trending-itconnect.component.css']
})
export class TrendingItConnectComponent implements OnInit, OnDestroy {
  private subs: SubSink;
  consts = TRENDING_ITCONNECT_COMPONENT_CONSTS;
  Qlinkconsts = QUICK_LINKS_COMPONENT_CONSTS;
  sortOptions = [this.consts.LABEL_SORT_MOST, this.consts.LABEL_SORT_LATEST, this.consts.LABEL_SORT_NEW];
  ratingOptions = [];
  kbOptions = [];
  categoryOptions = [];

  isLoading = false;
  filterDisabled = false;

  sortControl = new FormControl(this.consts.LABEL_SORT_MOST);
  ratingControl = new FormControl();
  kbControl = new FormControl();
  categoryControl = new FormControl();
  
  //2296-TH-2295--disable_country_selection_for_UserProfile/MyProfiles/TrendingLink(ITconnect/UPtime)
  // Get the IT Support link of ITConnect or UPtime from the QuickLinks_json file
  /*
  ITSupportLink$ = combineLatest([this.peopleService.currentUser$, this.quickLinksService.quickLinks$]).pipe(
    switchMap(([user, links]) => {
      const country = user?.country || '';
      const idUPtime = links.findIndex((qlink) => qlink.link.includes(this.Qlinkconsts.UPTIME_URL));
      const ITSupportLink = links[idUPtime].country.includes(country)
        ? this.Qlinkconsts.UPTIME_URL
        : this.Qlinkconsts.ITCONNECT_URL;
      return of(ITSupportLink);
    })
  );
 */

  ITSupportLink$ = this.quickLinksService.quickLinkList$.pipe();

  ITArticles$ = this.searchDataService.getTrendingITArticles().pipe(share());

  articleList$ = combineLatest([
    this.sortControl.valueChanges.pipe(startWith(this.consts.LABEL_SORT_MOST)),
    this.ratingControl.valueChanges.pipe(startWith(<string>null)),
    this.kbControl.valueChanges.pipe(startWith(<string>null)),
    this.categoryControl.valueChanges.pipe(startWith(<string>null))
  ]).pipe(
    distinctUntilChanged(),
    tap(() => {
      this.isLoading = true;
      this.disableFilters();
    }),
    debounceTime(500),
    switchMap(([sort, rating, kb, category]) =>
      this.ITArticles$.pipe(map((results) => this.sortResults(sort, rating, kb, category, results)))
    ),
    tap(() => {
      this.isLoading = false;
      this.enableFilters();
    }),
    share()
  );

  categoryFilters$ = this.articleFiltersBase('kbcategory');
  ratingFilters$ = this.articleList$.pipe(
    map((articles) => [...new Set(articles.map((a) => Math.floor(a['rating'])).sort((a, b) => b - a))])
  );
  kbFilters$ = this.articleFiltersBase('kbknowledgebase');

  articleFiltersBase(property: string): Observable<string[]> {
    return this.articleList$.pipe(map((articles) => this.uniqueArticleFilters(articles, property)));
  }

  uniqueArticleFilters(articles: IItArticles[], field: string): string[] {
    return articles.reduce((prev: string[], curr: IItArticles) => {
      if (!prev.some((e) => e === curr[field])) {
        curr[field] !== undefined && prev.push(curr[field]);
      }
      return prev.sort();
    }, []);
  }

  sortResults(
    sort: string,
    rating: number | string,
    kb: string,
    category: string,
    results: IItArticles[]
  ): IItArticles[] {
    if (sort === this.consts.LABEL_SORT_NEW) {
      results = results.sort((a, b) => new Date(b.syscreatedon).getTime() - new Date(a.syscreatedon).getTime());
    } else if (sort === this.consts.LABEL_SORT_MOST) {
      results = results.sort((a, b) => b.sysviewcount - a.sysviewcount);
    } else {
      results = results.sort((a, b) => new Date(b.sysupdatedon).getTime() - new Date(a.sysupdatedon).getTime());
    }

    if (rating !== null) {
      results = results.filter((r) => Math.floor(r.rating) === (rating as number));
    }
    if (category) {
      results = results.filter((c) => c.kbcategory === category);
    }
    if (kb) {
      results = results.filter((c) => c.kbknowledgebase === kb);
    }

    return results;
  }

  disableFilters(): void {
    this.sortControl.disable({ emitEvent: false });
    this.ratingControl.disable({ emitEvent: false });
    this.kbControl.disable({ emitEvent: false });
    this.categoryControl.disable({ emitEvent: false });
  }

  enableFilters(): void {
    this.sortControl.enable({ emitEvent: false });
    this.ratingControl.enable({ emitEvent: false });
    this.kbControl.enable({ emitEvent: false });
    this.categoryControl.enable({ emitEvent: false });
  }

  clearFilters(emitEvent: boolean): void {
    this.sortControl.setValue(this.consts.LABEL_SORT_MOST, { emitEvent });
    this.sortControl.markAsPristine();
    this.ratingControl.setValue(null, { emitEvent });
    this.ratingControl.markAsPristine();
    this.kbControl.setValue(null, { emitEvent });
    this.kbControl.markAsPristine();
    this.categoryControl.setValue(null, { emitEvent });
    this.categoryControl.markAsPristine();
  }

  isFiltersDirty(): boolean {
    return this.sortControl.dirty || this.ratingControl.dirty || this.kbControl.dirty || this.categoryControl.dirty;
  }

  constructor(
    private searchDataService: SearchDataService,
    private quickLinksService: QuickLinksService,
    private peopleService: PeopleService
  ) {}

  ngOnInit(): void {
    this.subs = new SubSink();

    // Category dropdown options
    this.subs.sink = this.categoryFilters$.subscribe((res) => {
      if (this.categoryOptions.length === 0) {
        this.categoryOptions = res;
      }
    });

    // Rating dropdown options
    this.subs.sink = this.ratingFilters$.subscribe((res) => {
      if (this.ratingOptions.length === 0) {
        this.ratingOptions = res;
      }
    });

    // Knowledge base (KB) dropdown options
    this.subs.sink = this.kbFilters$.subscribe((res) => {
      if (this.kbOptions.length === 0) {
        this.kbOptions = res;
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  openLink(): void {
    this.subs.sink = this.ITSupportLink$.subscribe((res) => {
      window.open(`${this.Qlinkconsts.URL_PROTOCOL}${this.Qlinkconsts.UPTIME_URL}`, '_blank');
    });
  }
}
